import React, { useEffect, memo, useRef, useState } from 'react';
import { message, Spin, InputNumber, Typography, Form, Card, Input, Checkbox, Col, Row, Divider } from 'antd';
import { useDispatch } from 'react-redux';
import { sleep, sentenceCase } from 'utils';
import providerService from 'services/providers.service';
import { actions as centerActions } from 'features/add-center';
import { ADD_APPLICATION_STEPS } from 'constants/index';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { dependencyNumberValidator } from 'utils';
import Select from 'components/Select';
import { AsyncSelect } from 'components/Select';
import { Banner } from 'components/ui/Banner';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';
import RadioCards from 'components/RadioCards/RadioCards';

const { Paragraph } = Typography;

export const REGISTRATION_FEE_ID = 325;

const discountMap = {
  'Sibling discount': { id: 87, required: true, order: 1 },
  'Military discount': { id: 86, required: true, order: 2 },
  'Employee discount': { id: 88, required: true, order: 3 },
  'Corporate discount': { id: 89, required: true, order: 4 },
  'Promotional discount': { id: 90, required: true, order: 5 },
  'Other discount': { id: 91, required: false, order: 6 },
};

const AddFee = React.memo(({ index, readOnly }) => {
  return (
    <>
      <Row gutter={[20, 0]} className="form-row">
        <Col xs={24} md={12} xl={8}>
          <Form.Item
            name={[index, 'chargedBy']}
            label="Charged per"
            rules={[{ required: true, message: 'Charged per is required.' }]}
          >
            <AsyncSelect
              optionsApiUrl="/options/36"
              ariaLabel="Charged per"
              name={[index, 'chargedBy']}
              disabled={readOnly}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={16}>
          <Form.Item
            name={[index, 'description']}
            label="Additional comments"
            rules={[{ max: 225, message: 'Description is too long.' }]}
            disabled={readOnly}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} xl={8}>
          <Form.Item
            name={[index, 'frequency']}
            label="Frequency"
            rules={[{ required: true, message: 'Frequency is required.' }]}
          >
            <AsyncSelect
              optionsApiUrl="/options/35"
              ariaLabel="Frequency"
              name={['feeRates', index, 'frequency']}
              disabled={readOnly}
              optionFilter={(option) => {
                const lowerCaseTitle = option.title.toLowerCase();
                return lowerCaseTitle.includes('initial') || lowerCaseTitle.includes('annual');
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            label="Amount"
            name={[index, 'amount']}
            rules={[{ required: true, message: 'Amount is required.' }]}
            disabled={readOnly}
          >
            <InputNumber
              placeholder="Amount"
              min={0}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
});

const MCCYNDiscountsStep = ({
  application,
  setApplication,
  id,
  setStep,
  isFormSubmitting,
  loading,
  applicationSubmitted,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();
  const [discountTypes, setDiscountTypes] = useState([]);
  const [discountError, setDiscountError] = useState(null);
  const [fetchedFees, setFetchedFees] = useState([]);
  const [fetchedDiscounts, setFetchedDiscounts] = useState([]);

  useEffect(() => {
    if (application) {
      form.setFieldsValue({
        ...application,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, form]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let [discountTypes, feeRates, applicationDiscounts] = await Promise.all([
          providerService.getDiscountTypes(),
          providerService.getFeeRates(id),
          providerService.getApplicationDiscounts(id),
        ]);

        setDiscountTypes(discountTypes);
        setFetchedFees(feeRates);
        setFetchedDiscounts(applicationDiscounts);

        //Setting fee rates
        const feeRateValues = feeRates?.length
          ? { feeRates, doYouChargeFees: true }
          : {
              feeRates: [{ name: 0, key: 0, isListField: true, fieldKey: 0 }],
              doYouChargeFees: applicationSubmitted && feeRates.length === 0 ? false : undefined,
            };

        form.setFieldsValue(feeRateValues);

        //Setting discount values
        let discountValues = { discounts: [] };

        if (applicationDiscounts && applicationDiscounts.length > 0) {
          const idToOrderMap = Object.values(discountMap).reduce((acc, { id, order }) => {
            acc[id] = order;
            return acc;
          }, {});

          applicationDiscounts = applicationDiscounts.filter(
            (discount) => discount?.type && idToOrderMap[discount.type.id],
          );

          //Sort discounts by order defined in discountMap
          if (applicationDiscounts.length > 0) {
            const orderedDiscounts = Object.values(discountMap)
              .sort((a, b) => a.order - b.order)
              .map(({ id }) => {
                const existingDiscount = applicationDiscounts.find((discount) => discount.type.id === id);
                return existingDiscount || null;
              });

            discountValues = {
              discounts: orderedDiscounts,
              doYouOfferDiscounts: true,
            };
          } else if (applicationSubmitted) {
            discountValues.doYouOfferDiscounts = false;
          }
        }

        form.setFieldsValue(discountValues);
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFeeForm = Form.useWatch(['doYouChargeFees'], form);
  const toggleDiscountForm = Form.useWatch(['doYouOfferDiscounts'], form);

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  const validateDiscounts = (_, value) => {
    const errorMessage = 'You must enter at least one discount';
    try {
      if (!value || !Array.isArray(value)) {
        return Promise.resolve();
      }

      const filledRows = value.filter((row) => row.rate && row.unit);
      if (filledRows.length === 0) {
        return Promise.reject(new Error(errorMessage));
      }

      return Promise.resolve();
    } catch (err) {
      Promise.reject(new Error(errorMessage));
    }
  };

  const dependencyRateValidator =
    (dependencyKey) =>
    ({ getFieldValue }) => ({
      validator(_, value) {
        const unit = getFieldValue(dependencyKey);
        if (unit === 'RATE' && value > 100) {
          return Promise.reject('Enter a number less than or equal to 100.');
        }
        return Promise.resolve();
      },
    });

  return (
    <div className="w-full">
      <Spin spinning={isFormSubmitting || loading}>
        <div>
          <Form
            layout="vertical"
            form={form}
            disabled={applicationSubmitted}
            onFinish={async (values) => {
              //Feeding the hardcoded type since its the only type we want
              const feeType = {
                id: REGISTRATION_FEE_ID,
                sequenceNumber: 1,
                title: 'Registration Fee',
                description: 'Registration Fee',
              };

              if (values?.feeRates?.length && values.feeRates[0]) {
                values.feeRates[0].type = feeType;
              }

              try {
                //Filter out discounts and fee rates
                const { discounts, feeRates, ...filteredValues } = values;
                dispatch(centerActions.setIsFormSubmitting(true));
                const updatedCenterData = await providerService.updateApplication(id, {
                  ...application,
                  ...filteredValues,
                });

                setApplication(updatedCenterData);

                //TODO - consider a batch update for fees and discounts
                // Handle Fees
                if (!toggleFeeForm) {
                  // If doYouChargeFees is false, delete all fees
                  await Promise.all(fetchedFees.map((fee) => providerService.deleteFeeRate(id, fee.id)));
                } else if (feeRates.length) {
                  await Promise.all(
                    feeRates.map((fee) => providerService.addUpdateFeeRate(id, { ...fee, application: { id } })),
                  );
                }

                // Handle Discounts
                if (!toggleDiscountForm) {
                  await Promise.all(
                    fetchedDiscounts.map((discount) => providerService.deleteApplicationDiscounts(id, discount.id)),
                  );
                } else if (discounts.length) {
                  await Promise.all(
                    discounts.map(async (discount) => {
                      if (discount && discount.id && !discount.rate) {
                        // Delete discount if it has an ID but rate is empty
                        return providerService.deleteApplicationDiscounts(id, discount.id);
                      } else if (discount && discount.rate && discount.unit) {
                        const dType = discountTypes.find((type) => type.id === discount.typeId);
                        discount.type = dType;
                        discount.discountOffered = true;
                        if (discount.type) {
                          return providerService.addUpdateApplicationDiscounts(id, {
                            ...discount,
                            application: { id },
                          });
                        }
                      }
                    }),
                  );
                }

                if (shouldGoToNextStep.current) {
                  setStep(ADD_APPLICATION_STEPS.AGREEMENT);
                }

                message.success('Saved');

                await sleep(100);
              } catch (error) {
                newrelic.noticeError(error);
                message.error('Unable to save center details.');
              } finally {
                dispatch(centerActions.setIsFormSubmitting(false));
              }
            }}
          >
            {(form) => {
              return (
                <div>
                  <div className="p-7">
                    <div className="hidden">
                      <Form.Item name={['application', 'id']}>
                        <InputNumber />
                      </Form.Item>
                    </div>

                    <Banner question="Fees and discounts" className="mb-12" />

                    {/*Fee Section */}
                    <Card className="mb-8" data-testid="feeSection" title="Registration fees">
                      <Form.Item
                        label={
                          <>
                            Do you charge a registration fee that
                            <span class="font-extrabold">&nbsp;does not&nbsp;</span>
                            include the cost of deposits, supplies, activities, transportation, etc.?
                          </>
                        }
                        rules={[
                          {
                            required: true,
                            message: 'Please specify whether you charge a registration fee',
                          },
                        ]}
                        name="doYouChargeFees"
                        className="mb-1"
                      >
                        <RadioCards
                          className="mb-4"
                          options={[
                            {
                              value: true,
                              label: 'Yes',
                              'data-testid': 'feeYes',
                            },
                            {
                              value: false,
                              label: 'No',
                              'data-testid': 'feeNo',
                            },
                          ]}
                          data-testid="feeRadioButtons"
                        />
                      </Form.Item>

                      <FormHelperText text="Do not include deposits, supplies, activities, transportation, or similar costs. Registration fee reimbursement policy varies by military branch." />

                      {toggleFeeForm && (
                        <>
                          <Divider className="mt-10 mb-8" />

                          <div className="mt-9">
                            <Typography.Text type="secondary" className="block mb-8">
                              Enter all applicable registration fees below
                            </Typography.Text>

                            <Form.List name="feeRates">
                              {(feeRates) => (
                                <>
                                  {feeRates.map((field, index) => (
                                    <AddFee key={field.key} index={index} readOnly={applicationSubmitted} />
                                  ))}
                                </>
                              )}
                            </Form.List>
                          </div>
                        </>
                      )}
                    </Card>

                    {/*Discount Section */}
                    <Card className="mb-8" data-testid="feeSection" title="Discount information">
                      <Form.Item
                        label="Do you offer discounts of any kind?"
                        rules={[
                          {
                            required: true,
                            message: 'This is required',
                          },
                        ]}
                        name="doYouOfferDiscounts"
                        className="mb-1"
                      >
                        <RadioCards
                          className="mb-4"
                          options={[
                            {
                              value: true,
                              label: 'Yes',
                              'data-testid': 'discountYes',
                            },
                            {
                              value: false,
                              label: 'No',
                              'data-testid': 'discountNo',
                            },
                          ]}
                          data-testid="discountRadioButtons"
                        />
                      </Form.Item>

                      <FormHelperText
                        text="Any discounts offered to families who are paying private must also be offered to families
                        receiving military fee assistance. Military fee assistance is not a discount program. Assistance
                        + family co-pay = your full rate."
                      />

                      {toggleDiscountForm && (
                        <div>
                          <Divider />

                          <Paragraph>
                            Enter all applicable discounts below. Any discounts offered to families who are paying
                            privately must also be offered to families receiving military fee assistance. Military fee
                            assistance is not a discount program. Assistance + family co-pay = your full rate.
                          </Paragraph>

                          <div className="mb-10" />

                          <Form.List
                            name="discounts"
                            rules={[
                              {
                                validator: async (_, value) => {
                                  try {
                                    await validateDiscounts(_, value);
                                    setDiscountError(null);
                                  } catch (err) {
                                    setDiscountError(err.message);
                                    throw err;
                                  }
                                },
                              },
                            ]}
                          >
                            {(fields, { errors }) => (
                              <div className="flex flex-col gap-4">
                                {Object.entries(discountMap).map(([desc, { id, required }], index) => {
                                  return (
                                    <div key={id}>
                                      <Form.Item name={[index, 'id']} label="id" hidden>
                                        <InputNumber min={1} disabled />
                                      </Form.Item>

                                      <Form.Item
                                        name={[index, 'typeId']}
                                        label="typeId"
                                        hidden
                                        initialValue={id}
                                      ></Form.Item>

                                      <div className="flex gap-x-20 flex-wrap">
                                        <Form.Item
                                          key={id}
                                          name={[index, 'rate']}
                                          label={sentenceCase(desc)}
                                          rules={[
                                            dependencyNumberValidator(['discounts', index, 'rate']),
                                            dependencyRateValidator(['discounts', index, 'unit']),
                                          ]}
                                          className="w-full lg:w-1/3 xl:w-1/4"
                                          disabled={applicationSubmitted}
                                          dependencies={[['discounts', index, 'unit']]}
                                        >
                                          <Input
                                            placeholder=""
                                            data-testid={`${desc.replace(/\s/g, '').toLowerCase()}`}
                                          />
                                        </Form.Item>

                                        <Form.Item
                                          name={[index, 'unit']}
                                          label="Unit"
                                          className="w-full lg:w-1/3 xl:w-1/4"
                                        >
                                          <Select
                                            name={`unit`}
                                            options={[
                                              { title: '$ - Dollars', id: 'AMOUNT' },
                                              { title: '% - Percentage', id: 'RATE' },
                                            ]}
                                            isOptionSelected={(opt, [value]) => opt?.id === value}
                                            onChangeFormatter={(v) => v?.id}
                                            ariaLabel="Rate/Amount"
                                            disabled={applicationSubmitted}
                                          />
                                        </Form.Item>

                                        <Form.Item
                                          name={[index, 'additionalComments']}
                                          label="Additional comments"
                                          className="w-full xl:w-1/3"
                                        >
                                          <Input.TextArea rows={3} disabled={applicationSubmitted} className="w-full" />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </Form.List>

                          {discountError && (
                            <Form.Item>
                              <span className="ant-form-item-explain-error">{discountError}</span>
                            </Form.Item>
                          )}
                        </div>
                      )}
                    </Card>

                    <Card className="mb-8" data-testid="costsSection" title="Included costs">
                      <Form.Item
                        label="Does your facility include the cost of lunch in tuition?"
                        name="lunchIncluded"
                        className="mb-0"
                        rules={[
                          {
                            required: true,
                            message: 'This is required',
                          },
                        ]}
                      >
                        <RadioCards
                          className="mb-4"
                          options={[
                            {
                              value: true,
                              label: 'Yes',
                              'data-testid': 'costLunchYes',
                            },
                            {
                              value: false,
                              label: 'No',
                              'data-testid': 'costLunchNo',
                            },
                          ]}
                          data-testid="costLunchRadioButtons"
                        />
                      </Form.Item>

                      <Form.Item
                        label="Does your facility include the cost of formula for infants (6 weeks - 12 months) in tuition?"
                        name="formulaFeeIncluded"
                        className="mt-8 mb-0"
                        rules={[
                          {
                            required: true,
                            message: 'This is required',
                          },
                        ]}
                      >
                        <RadioCards
                          className="mb-4"
                          options={[
                            {
                              value: true,
                              label: 'Yes',
                              'data-testid': 'costFormulaYes',
                            },
                            {
                              value: false,
                              label: 'No',
                              'data-testid': 'costFormulaNo',
                            },
                          ]}
                          data-testid="costFormulaRadioButtons"
                        />
                      </Form.Item>
                    </Card>

                    <Card className="mb-8" title="Confirm answers">
                      <Form.Item
                        name="agreeToDiscountTerms"
                        rules={[
                          () => ({
                            validator(_, value) {
                              if (value) {
                                return Promise.resolve();
                              }

                              return Promise.reject('Please confirm your answers');
                            },
                          }),
                        ]}
                        valuePropName="checked"
                      >
                        <Checkbox
                          disabled={applicationSubmitted}
                          data-testid="agreeToDiscountTerms"
                          className="text-[15px]"
                        >
                          <span className="text-red-500 mr-1">*</span>I certify that all above information is correct
                          and all discounts that I offer are listed. I understand that any changes to the discounts
                          listed on this form must be reported to Child Care Aware of America. I certify that any
                          registration fees listed on this form are used solely to hold the spot for a family and are
                          not applied to their child care tuition, facility maintenance/supplies, or activities.
                        </Checkbox>
                      </Form.Item>
                    </Card>
                  </div>

                  <ApplicationWizardActions
                    applicationIsReadOnly={applicationSubmitted}
                    backButtonProps={{
                      disabled: false,
                      onClick: () => setStep(ADD_APPLICATION_STEPS.RATES),
                      'data-testid': 'backButton',
                    }}
                    nextButtonProps={{
                      loading: isFormSubmitting,
                      disabled: isFormSubmitting || isSubmitDisabled,
                      onClick: () => {
                        if (applicationSubmitted) {
                          setStep(ADD_APPLICATION_STEPS.AGREEMENT);
                          return;
                        }

                        shouldGoToNextStep.current = true;
                      },
                      'data-testid': 'discounts-next-form-submit',
                    }}
                  />
                </div>
              );
            }}
          </Form>
        </div>
      </Spin>
    </div>
  );
};

export default memo(MCCYNDiscountsStep);
